import HttpService from 'services/HttpService';
import {
    dispatchCustomEvent,
    isVideoPlaying,
    showToast,
    isBrowserNotificationGranted,
    getDivVisibility,
    handleBrazeNotification,
    interpolateUrl,
} from './helpers';
import { trackAction } from 'helpers/trackingOM';
import { JsCarousel } from 'components/carousels/JsCarousel';
import { getLocale } from 'services/LocalizationService';
import WatchlistCta from 'components/watchlist/WatchlistCta';
import PreferencesCta from 'components/preferences/PreferencesCta';
import { storeSearchTerm } from 'helpers/searchPageHelper';

// START A/B TEST PLGRWEB-1647-SearchPage-Previous Searches
import {
    initializePreviousSearchInstance,
    trackRecentSearchCarousel,
} from 'abTests/testClasses/SearchPagePreviousSearch';
// END A/B TEST PLGRWEB-1647-SearchPage-Previous Searches

// handle highlight expansion and events
// mixin pattern to allow for multiple inheritance
const Highlight = (SubClass) =>
    class extends SubClass {
        static HandleExpansionTimeout = null;
        static MY_LIST_CAROUSEL_EVENT_TYPE = 'refreshMyListCarousel';

        constructor(element, options) {
            super(element, options);
            this.disableOnScroll = true;
            this.expandedElement = null;
            this._http = new HttpService();
            this.watchlistService = window._watchlistService;
            this.notificationService = window._notificationService;
            this.template = null;
            this.type = '';
            this.allowTrackVideoPreviewEnd = false;
        }

        __initElement(element, data) {
            // remove all other events in case of duplication
            element.removeEventListener('mouseenter', null);
            element.removeEventListener('mouseleave', null);

            element.addEventListener('mouseenter', () => {
                let percentageNotVisible = 100 - getDivVisibility($(element));

                // disable for mobile width devices
                if (
                    window.innerWidth < 768 ||
                    document.body.getAttribute('touch-enabled') === 'true' ||
                    percentageNotVisible > 50 ||
                    this._wrapper?.find('.video-carousel-box').hasClass('editOn')
                ) {
                    return;
                }

                this.element = element;

                clearTimeout(Highlight.HandleExpansionTimeout);
                Highlight.HandleExpansionTimeout = setTimeout(() => {
                    // cleanup old containers if needed
                    document
                        .querySelectorAll(`.${this.type}-expanded-container`)
                        .forEach((el) => el.remove());
                    if (this.isKeepWatching && !data.isMovie) {
                        this.currentElementId = data.cmsId ?? '';
                    } else {
                        this.currentElementId =
                            data.id !== null && data.id !== '' ? data.id : data.contentId;
                    }
                    const expandedElement = this.__createExpandedElement(element, data);

                    this.__initExpandedElement(element, expandedElement);
                }, 500);
            });

            element.addEventListener('mouseleave', () => {
                if (Highlight.HandleExpansionTimeout) {
                    clearTimeout(Highlight.HandleExpansionTimeout);
                }
            });

            // cancel expansion on carousel element click
            element.addEventListener('click', () => {
                clearTimeout(Highlight.HandleExpansionTimeout);
            });
        }

        // positioning from top handled by subclass
        __createExpandedElement(element, data) {
            let isEvent = element.classList.contains('is-event');
            let template = document.createElement('template');
            data.cType = this.type;
            data.isEvent = isEvent;
            template.innerHTML = this.template({ data, getLocale, interpolateUrl });
            let expandedElement = template.content.querySelectorAll(
                `.${this.type}-expanded-container`,
            )[0];

            const initial = element.getBoundingClientRect();

            let mainContainer = document.querySelector('body');
            mainContainer.appendChild(expandedElement);

            // expanded element has same dimensions as condensed view before expansion
            expandedElement.style.width = initial.width + 'px';
            expandedElement.style.height = initial.height + 'px';

            // expanded element has same position as condensed view before expansion
            let leftCoordinate = initial.x;
            expandedElement.style.left = leftCoordinate + 'px';
            expandedElement.style.opacity = 0;

            this.expandedElement = expandedElement;

            this.__createMediaManager(
                data,
                expandedElement.querySelector('.media-manager-container'),
            );

            return expandedElement;
        }

        __createMediaManager(data, target) {
            throw new Error('Highlight: Do not use parent class');
        }

        // positioning from top handled by subclass
        async __removeExpandedElement(element) {
            if (!this.expandedElement) {
                return;
            }

            const initial = element.getBoundingClientRect();

            // revert expanded container position and dimensions to condensed view
            let leftCoordinate = initial.x;
            this.expandedElement.style.left = leftCoordinate + 'px';

            this.expandedElement.style.width = `${initial.width}px`;
            this.expandedElement.style.height = `${initial.height}px`;

            // set z-index so element that is fading out does not overlap element that is fading in
            this.expandedElement.style.zIndex = '0';

            // fade out expanded container
            this.expandedElement.style.opacity = 0;
            this.expandedElement.querySelector(
                `.${this.type}-expanded-overlay-components`,
            ).style.opacity = 0;

            if (this.mediaManager) {
                let video = this.mediaManager.video;
                if (video && video.isPlaying()) {
                    document.body.dispatchEvent(JsCarousel.CarouselVideoStopEvent);
                    const track = this.__getVideoTracking(element, this.expandedElement, video);
                    this.__handleTrackVideoPreviewEnd(track);
                }
                await this.mediaManager.destroy();
                this.mediaManager = null;
            }

            // reset state
            let currentExp = this.expandedElement;

            this.currentElementId = '';
            this.expandedElement = null;

            // remove from dom
            setTimeout(() => {
                currentExp.remove();
            }, 300);
        }

        __initExpandedElement(element, expandedElement) {
            this.__expandElement(element, expandedElement);
            this.__handleVideo(element, expandedElement);
            this.__initializeClickEvents(element, expandedElement);
            this.__initializePrimaryCtaAction(element, expandedElement);
            this.__initializeMyListCtaAction(element, expandedElement);
            this.__initializeNotificationCtaAction(element, expandedElement);
        }

        __expandElement(element, expandedElement, growthRate) {
            throw new Error('Highlight: Do not use parent class');
        }

        __handleTrackVideoPreviewEnd(track) {
            if (this.allowTrackVideoPreviewEnd) {
                trackAction('trackVideoPreviewEnd', track);
                this.allowTrackVideoPreviewEnd = false;
            }
        }

        __handleVideo(element, expandedElement) {
            this.mediaManager.onEndedCallback = (e) => {
                const elapsedTime = e.detail?.contentTime ?? 0;

                const track = this.__getVideoTracking(
                    element,
                    expandedElement,
                    this.mediaManager.video,
                    elapsedTime,
                );
                this.__handleTrackVideoPreviewEnd(track);
                document.body.dispatchEvent(JsCarousel.CarouselVideoStopEvent);
            };

            this.mediaManager.onPlayingCallback = () => {
                const track = this.__getVideoTracking(
                    element,
                    expandedElement,
                    this.mediaManager.video,
                );
                trackAction('trackVideoPreviewStart', track);
                this.allowTrackVideoPreviewEnd = true;
                document.body.dispatchEvent(JsCarousel.CarouselVideoPlayEvent);
            };

            this.mediaManager.onMuteCallback = () => {
                trackAction('trackMutePlayer', this.__getTrackingSpecs(element, expandedElement));
                localStorage.setItem('spliceVolumeOn', '0');
            };

            this.mediaManager.onUnmuteCallback = () => {
                trackAction('trackUnmutePlayer', this.__getTrackingSpecs(element, expandedElement));
                localStorage.setItem('spliceVolumeOn', '1');
            };
            this.mediaManager.init();
        }

        __getVideoTracking(element, expandedElement, video, videoCurrentTime = null) {
            let track = this.__getTrackingSpecs(element, expandedElement);

            if (!video) {
                return track;
            }

            let currentTime = videoCurrentTime ?? video?.currentTime();
            let elapsedTime = currentTime ?? 0;
            elapsedTime = elapsedTime.toString();

            track = {
                ...track,
                ...{
                    elapsedTime,
                },
            };
            return track;
        }

        __initializePrimaryCtaAction(element, expandedElement) {
            let elementLink = element.querySelectorAll('a')[0] ?? null;
            if (typeof element !== 'undefined' && element.tagName === 'A') {
                elementLink = element;
            }
            const primaryCta = expandedElement.querySelector('.highlight-play-button') ?? null;
            let url = primaryCta.getAttribute('button-url');

            if (!elementLink) {
                return;
            }

            const fallbackUrl = elementLink.getAttribute('href');
            const ctaText = elementLink.getAttribute('data-cta');

            if (!url && !fallbackUrl) {
                return;
            }

            if (url.length < 1) {
                url = fallbackUrl;
            }

            primaryCta.addEventListener('click', async (evt) => {
                evt.stopPropagation();
                let trackingObj = this.__getTrackingSpecs(element, expandedElement);
                this.__handleImpressionTrackingClick(element);

                // START A/B TEST PLGRWEB-1647-SearchPage-Previous Searches
                //store this tile's movieContentId /showId into the local storage
                //then sends to PHP store in cookie
                const prevSearchInstance = initializePreviousSearchInstance();
                //only applies to search grid
                if (
                    prevSearchInstance &&
                    element.parentNode.classList.contains('search__results--results')
                ) {
                    trackingObj = trackRecentSearchCarousel(trackingObj);
                    await prevSearchInstance.storeThisTile(element, evt, true);
                }
                // END A/B TEST PLGRWEB-1647-SearchPage-Previous Searches

                trackAction(this.__getTrackingEventString(), { ...trackingObj, ctaText });

                let video = this.mediaManager.video;
                if (video && video.isPlaying()) {
                    // START A/B TEST feature/PLGWEB-1350-continous-sizzle-trailer-static-image
                    if (this.continuousSizzleTrailer) {
                        this.continuousSizzleTrailer
                            .setVideoLeavingTime({ video, expandedElement })
                            .setVideoDataIntoLocalStorage();
                    }
                    // END A/B TEST feature/PLGWEB-1350-continous-sizzle-trailer-static-image
                    const track = this.__getVideoTracking(
                        element,
                        expandedElement,
                        this.mediaManager.video,
                    );
                    this.__handleTrackVideoPreviewEnd(track);
                }

                if (this.searchUXImprovements) {
                    this.__handleSaveSearchQuery(element);
                }

                window.location.assign(url);
            });
        }

        __initializeMyListCtaAction(element, expandedElement) {
            const watchListCta = expandedElement.querySelector('.highlight-watchlist-cta') ?? null;
            let isClicked = false;
            if (watchListCta === null) {
                return;
            }

            watchListCta.addEventListener('click', (evt) => {
                if (isClicked) {
                    return;
                }

                evt.stopPropagation();
                isClicked = true;
                const linkActive = watchListCta.parentElement.classList.contains('active');

                if (!linkActive) {
                    trackAction(
                        'trackAddWatchList',
                        this.__getTrackingSpecs(element, expandedElement),
                    );
                } else {
                    trackAction(
                        'trackRemoveWatchList',
                        this.__getTrackingSpecs(element, expandedElement),
                    );
                }

                let elementId = this.currentElementId;
                let spreadStateId = elementId;

                if (this.isKeepWatching) {
                    spreadStateId = element.querySelector('a').getAttribute('id');
                }

                if (!linkActive) {
                    const url =
                        `${CBS.Registry.region.prefix}` + watchListCta.getAttribute('href-add');

                    this.watchlistService
                        .addContentToWatchlist(elementId, url)
                        .then(({ data }) => {
                            if (data.success) {
                                this.__spreadStateEvent(
                                    spreadStateId,
                                    WatchlistCta.IN_WATCHLIST_CLASS,
                                );
                                dispatchCustomEvent(element, Highlight.MY_LIST_CAROUSEL_EVENT_TYPE);
                                dispatchCustomEvent(element, Highlight.STATE_CAROUSEL_EVENT_TYPE);
                            }

                            isClicked = false;
                        })
                        .catch((e) => {
                            isClicked = false;
                        });
                } else {
                    const url =
                        `${CBS.Registry.region.prefix}` + watchListCta.getAttribute('href-remove');
                    this.watchlistService
                        .removeContentFromWatchlist(elementId, url)
                        .then(({ data }) => {
                            if (data.success) {
                                this.__spreadStateEvent(
                                    spreadStateId,
                                    WatchlistCta.IN_WATCHLIST_CLASS,
                                );
                                dispatchCustomEvent(element, Highlight.MY_LIST_CAROUSEL_EVENT_TYPE);
                                dispatchCustomEvent(element, Highlight.STATE_CAROUSEL_EVENT_TYPE);
                            }

                            isClicked = false;
                        })
                        .catch((e) => {
                            isClicked = false;
                        });
                }
            });
        }

        __initializeNotificationCtaAction(element, expandedElement) {
            const notificationCta =
                expandedElement.querySelector('.highlight-notify-bell-cta') ?? null;
            const tileLink = element.querySelector('.link, .focusable') ?? element;
            const tileTitle = tileLink?.getAttribute('title') ?? '';
            // 6 for search results, 10 for carousels
            const notificationId =
                tileLink?.getAttribute('data-impression').split('|')[10] ??
                tileLink?.getAttribute('data-impression').split('|')[6];

            let isClicked = false;

            if (!notificationCta) {
                return;
            }

            const clickAction = () => {
                isClicked = true;

                if (!isBrowserNotificationGranted()) {
                    showToast(PreferencesCta.CTA_ERROR_NOTIFICATIONS_DISABLED_COPY, false);
                    setTimeout(() => {
                        isClicked = false;
                    }, 1000);
                    return;
                }

                const linkActive = notificationCta.parentElement.classList.contains('active');

                if (!linkActive) {
                    trackAction(
                        'trackReminderSelect',
                        this.__getTrackingSpecs(element, expandedElement),
                    );
                } else {
                    trackAction(
                        'trackReminderDeSelect',
                        this.__getTrackingSpecs(element, expandedElement),
                    );
                }

                let elementId = this.currentElementId;
                let spreadStateId = elementId;

                if (this.isKeepWatching) {
                    spreadStateId = element.querySelector('a').getAttribute('id');
                }

                if (!linkActive) {
                    const url =
                        `${CBS.Registry.region.prefix}` +
                        notificationCta.getAttribute('href-add') +
                        '?title=' +
                        tileTitle;

                    this.notificationService
                        .addContentToNotificationList(notificationId, url)
                        .then(({ data }) => {
                            if (data.success) {
                                this.__spreadStateEvent(
                                    spreadStateId,
                                    PreferencesCta.NOTIFY_ON_CLASS,
                                );
                                showToast(data.message, data.success);
                                dispatchCustomEvent(element, Highlight.STATE_CAROUSEL_EVENT_TYPE);
                            }

                            isClicked = false;
                        })
                        .catch((e) => {
                            isClicked = false;
                            showToast(PreferencesCta.CTA_ERROR_COPY, false);
                        });
                } else {
                    const url =
                        `${CBS.Registry.region.prefix}` +
                        notificationCta.getAttribute('href-remove') +
                        '?title=' +
                        tileTitle;

                    this.notificationService
                        .removeContentFromNotificationList(notificationId, url)
                        .then(({ data }) => {
                            if (data.success) {
                                this.__spreadStateEvent(
                                    spreadStateId,
                                    PreferencesCta.NOTIFY_ON_CLASS,
                                );
                                showToast(data.message, data.success);
                                dispatchCustomEvent(element, Highlight.STATE_CAROUSEL_EVENT_TYPE);
                            }

                            isClicked = false;
                        })
                        .catch((e) => {
                            isClicked = false;
                            showToast(PreferencesCta.CTA_ERROR_COPY, false);
                        });
                }
            };

            notificationCta.addEventListener('click', (evt) => {
                evt.stopPropagation();

                if (!isClicked) {
                    handleBrazeNotification(clickAction);
                }
            });
        }

        __initializeClickEvents(element, expandedElement) {
            expandedElement.addEventListener(
                'click',
                async (event) => {
                    const classList = event.target.classList;
                    const parentClassList = event.target.parentElement.classList;
                    const bottomComponents = expandedElement.querySelector(
                        `.${this.type}-expanded-overlay-components`,
                    );
                    const primaryCta =
                        expandedElement.querySelector('.highlight-play-button') ?? null;
                    const elementLink = element.querySelectorAll('a')[0] ?? element;
                    const url = primaryCta.getAttribute('button-url');
                    const fallbackUrl = elementLink.getAttribute('href');
                    // block click of main anchor link if user clicking bottom component buttons
                    if (
                        bottomComponents.contains(event.target) ||
                        classList.contains('highlight-volume') ||
                        this.mediaManager?.volumeControl?.isEqualNode(event.target)
                    ) {
                        event.preventDefault();
                    }

                    if (
                        !classList.contains('highlight-play-button') &&
                        !parentClassList.contains('highlight-play-button') &&
                        !classList.contains('watchlist_copy') &&
                        !classList.contains('notify_copy') &&
                        !classList.contains('highlight-volume') &&
                        event.target.innerHTML !== 'WATCH NOW' &&
                        !this.mediaManager?.volumeControl?.isEqualNode(event.target)
                    ) {
                        this.__handleImpressionTrackingClick(element);
                        trackAction(
                            this.__getTrackingEventString(),
                            this.__getTrackingSpecs(element, expandedElement),
                        );

                        let track = this.__getVideoTracking(
                            element,
                            expandedElement,
                            this.mediaManager.video,
                        );

                        // START A/B TEST feature/PLGWEB-1350-continous-sizzle-trailer-static-image
                        if (this.continuousSizzleTrailer) {
                            const video = this.mediaManager.video;
                            this.continuousSizzleTrailer
                                .setVideoLeavingTime({ video, expandedElement })
                                .setVideoDataIntoLocalStorage();
                        }
                        // END A/B TEST feature/PLGWEB-1350-continous-sizzle-trailer-static-image

                        if (this.searchUXImprovements) {
                            this.__handleSaveSearchQuery(element);
                        }

                        // START A/B TEST PLGRWEB-1647-SearchPage-Previous Searches
                        //store this tile's movieContentId /showId into the local storage
                        //then sends to PHP store in cookie
                        const prevSearchInstance = initializePreviousSearchInstance();
                        if (
                            prevSearchInstance &&
                            element.parentNode.classList.contains('search__results--results')
                        ) {
                            track = trackRecentSearchCarousel(track);
                            await prevSearchInstance.storeThisTile(element, event, true);
                        }
                        // END A/B TEST PLGRWEB-1647-SearchPage-Previous Searches

                        this.__handleTrackVideoPreviewEnd(track);

                        if (event.target.innerHTML !== getLocale('upgrade_now')) {
                            window.location.assign(fallbackUrl ?? url ?? '');
                        }
                    }
                },
                true,
            );
        }

        __handleImpressionTrackingClick(element) {
            if (
                typeof this.impressionTracking !== 'undefined' &&
                this.impressionTracking.clickEvent
            ) {
                this.impressionTracking.clickEvent({
                    currentTarget: element,
                    data: { this: this.impressionTracking },
                });
            }

            if (
                typeof this.fathomTracking !== 'undefined' &&
                this.fathomTracking.clickEvent &&
                this.hasFathomTracking
            ) {
                const fathom = element
                    .querySelector('a.link')
                    .getAttribute('data-fathom')
                    ?.split('|');

                this.fathomTracking.clickEvent({
                    data: {
                        displayId: this.fathomTracking.display_id,
                        contentId: fathom[0]?.toString() ?? '',
                        contentTitle: fathom[5].toString(),
                    },
                });
            }
        }

        __getTrackingEventString() {
            throw new Error('Highlight: Do not use parent class');
        }

        __getTrackingSpecs() {
            throw new Error('Highlight: Do not use parent class');
        }

        __spreadStateEvent(id, toggleClassName) {
            throw new Error('Highlight: Do not use parent class');
        }

        __handleSaveSearchQuery(element) {
            // store search term to retain results if user navigates back
            const searchWrapper = document.querySelector('#search');
            const inputBarWrapper = searchWrapper?.querySelector('.search__inputBar');
            const input = inputBarWrapper?.querySelector('input');
            if (input) {
                storeSearchTerm(input.value, element.getAttribute('id'));
            }
        }
    };

export default Highlight;
